import {
    AuthContext,
    BaseButton,
    BaseCheckbox,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    EvidenceStatusEnum,
    GetProviderEvidenceListOrderEnum,
    GetProviderEvidenceListSortEnum,
    GetProviderEvidenceMissingListOrderEnum,
    GetProviderEvidenceMissingListSortEnum,
    getOptions,
    IFilterSchema,
    ISelectOption,
    ITableColumn,
    ITableFetchOptions,
    ProviderEvidence,
    ProviderMissingEvidence,
    ICustomFilter,
} from '@enter/core';
import { mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Row } from 'react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { DialogContext } from 'services';
import { IInjectedRootStore, IItemSortOrder, parseSortBy } from 'stores';
import { ClaimService } from 'stores/claim.service';
import { EvidenceViewDialog } from './evidence-view-dialog';
import { missingEvidenceColumns } from './missing-evidence-columns';
import * as S from './evidence-list.styles';

interface CurrentFetchOptions {
    pageIndex: number;
    pageSize: number;
    filters?: ICustomFilter[];
    sort?: string[];
    order?: string[];
}

const formatProviderName = (provider?: ProviderEvidence['billingProvider']): string => {
    if (!provider) {
        return '';
    }

    if (provider.organization) {
        return provider.organization;
    }

    const parts = [provider.first, provider.last].filter(Boolean);
    return parts.join(' ');
};

const formatRenderingProvider = (provider?: ProviderEvidence['renderingProvider']): string => {
    if (!provider) {
        return '';
    }

    const parts = [provider.first, provider.last].filter(Boolean);
    if (parts.length > 0) {
        return parts.join(' ');
    }

    return provider.organization || '';
};

const getEvidenceColumns = (
    onViewEvidence: (evidence: ProviderEvidence) => void,
): ITableColumn<ProviderEvidence, GetProviderEvidenceListSortEnum>[] => [
    {
        id: 'Id',
        Header: 'ID',
        accessor: (row): string => row.id || '',
        disableSortBy: true,
        Cell: DataTableCell('String'),
    },
    {
        id: 'EvidenceType',
        Header: 'Evidence Type',
        accessor: (row): string => row.evidenceType?.name || '',
        Cell: DataTableCell('String'),
        sortType: GetProviderEvidenceListSortEnum.EXPERIMENTID,
    },
    {
        id: 'Status',
        Header: 'Status',
        accessor: (row): string => row.status || '',
        Cell: DataTableCell('String'),
        sortType: GetProviderEvidenceListSortEnum.STATUS,
    },
    {
        id: 'PrimaryPayer',
        Header: 'Primary Payer',
        accessor: (row): string => row.primaryPayer?.name || '',
        Cell: DataTableCell('String'),
        sortType: GetProviderEvidenceListSortEnum.PRIMARYPAYERID,
    },
    {
        id: 'BillingProvider',
        Header: 'Billing Provider',
        accessor: (row): string => formatProviderName(row.billingProvider),
        Cell: DataTableCell('String'),
        sortType: GetProviderEvidenceListSortEnum.BILLINGPROVIDERID,
    },
    {
        id: 'RenderingProvider',
        Header: 'Rendering Provider',
        accessor: (row): string => formatRenderingProvider(row.renderingProvider),
        Cell: DataTableCell('String'),
        sortType: GetProviderEvidenceListSortEnum.RENDERINGPROVIDERID,
    },
    {
        id: 'Facility',
        Header: 'Facility',
        accessor: (row): string => row.facility?.name || '',
        Cell: DataTableCell('String'),
        sortType: GetProviderEvidenceListSortEnum.FACILITYID,
    },
    {
        id: 'Expired',
        Header: 'Expired',
        accessor: (row): string => row.expired || '',
        Cell: DataTableCell('DateTime'),
        sortType: GetProviderEvidenceListSortEnum.EXPIRED,
    },
    {
        id: 'Approved',
        Header: 'Approved',
        accessor: (row): string => row.approved || '',
        disableSortBy: true,
        Cell: DataTableCell('DateTime'),
    },
    {
        id: 'Denied',
        Header: 'Denied',
        accessor: (row): string => row.denied || '',
        disableSortBy: true,
        Cell: DataTableCell('DateTime'),
    },
    {
        id: 'Notes',
        Header: 'Notes',
        accessor: (row): string => row.notes || '',
        disableSortBy: true,
        Cell: DataTableCell('String'),
    },
    {
        id: 'Created',
        Header: 'Created',
        accessor: (row): string => row.created || '',
        Cell: DataTableCell('DateTime'),
        sortType: GetProviderEvidenceListSortEnum.CREATED,
    },
    {
        id: 'Action',
        Header: '',
        disableSortBy: true,
        Cell: ({ row }): React.ReactElement => {
            return (
                <div
                    onClick={(e): void => {
                        e.stopPropagation();
                    }}
                    onMouseDown={(e): void => {
                        e.stopPropagation();
                    }}
                >
                    <BaseButton
                        variant="text"
                        size="small"
                        onClick={(e): void => {
                            e.preventDefault();
                            e.stopPropagation();
                            onViewEvidence(row.original);
                        }}
                    >
                        <Icon path={mdiInformationOutline} size={0.8} style={{ marginRight: '4px' }} />
                        View
                    </BaseButton>
                </div>
            );
        },
    },
];

const evidenceFilterSchemas: IFilterSchema[] = [
    {
        label: 'ID',
        filterId: 'filterId',
        filterType: 'String',
        columnId: 'Id',
    },
    {
        label: 'Status',
        filterId: 'filterStatus',
        filterType: 'Select',
        options: getOptions(EvidenceStatusEnum),
        columnId: 'Status',
    },
    {
        label: 'Primary Payer',
        filterId: 'filterPrimaryPayerId',
        filterType: 'AsyncSelect',
        columnId: 'PrimaryPayer',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const payers = await ClaimService.queryPayers(query);
            return _.map(payers, p => ({
                label: p.name,
                value: p.id,
            }));
        },
    },
    {
        label: 'Billing Provider',
        filterId: 'filterBillingProviderId',
        filterType: 'AsyncSelect',
        columnId: 'BillingProvider',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const providers = await ClaimService.queryProviders(query);
            return _.map(providers, p => ({
                label: p.organization ? p.organization : `${p.first} ${p.last}`,
                value: p.id,
            }));
        },
    },
    {
        label: 'Rendering Provider',
        filterId: 'filterRenderingProviderId',
        filterType: 'AsyncSelect',
        columnId: 'RenderingProvider',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const providers = await ClaimService.queryProviders(query);
            return _.map(providers, p => ({
                label: p.organization ? p.organization : `${p.first} ${p.last}`,
                value: p.id,
            }));
        },
    },
    {
        label: 'Facility',
        filterId: 'filterFacilityId',
        filterType: 'AsyncSelect',
        columnId: 'Facility',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const facilities = await ClaimService.queryFacilities(query);
            return _.map(facilities, f => ({
                label: f.name,
                value: f.id,
            }));
        },
    },
    {
        label: 'Expired',
        filterId: 'filterExpired',
        filterType: 'DateTime',
        columnId: 'Expired',
    },
    {
        label: 'Created',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
];

type EvidenceListProps = {};
type IInjectedProps = EvidenceListProps & IInjectedRootStore;

export const EvidenceList = inject('rootStore')(
    observer((props: EvidenceListProps) => {
        const { rootStore } = props as IInjectedProps;
        if (!rootStore) {
            return <></>;
        }

        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const navigate = useNavigate();
        const { accountId } = useParams<{ accountId?: string }>();
        const [loading, setLoading] = useState(false);
        const [showMissingEvidence, setShowMissingEvidence] = useState(false);
        
        const currentFetchOptionsRef = useRef<CurrentFetchOptions>({
            pageIndex: 0,
            pageSize: 25,
        });

        const handleViewEvidence = useCallback(
            (evidence: ProviderEvidence): void => {
                dialogContext.showDialog?.({
                    props: {
                        onClose: (): void => {
                            dialogContext.hideDialog?.();
                        },
                        disableBackdropClick: false,
                        disableEscapeKeyDown: false,
                        renderTitle: 'View Evidence',
                    },
                    Component: <EvidenceViewDialog evidence={evidence} onClose={(): void => dialogContext.hideDialog?.()} />,
                });
            },
            [dialogContext],
        );

        const evidenceColumns = useMemo(
            () => getEvidenceColumns(handleViewEvidence),
            [handleViewEvidence],
        );

        const presenter = useRef(
            new DataTablePresenter<ProviderEvidence>({
                name: 'provider-evidence',
                data: [],
                columns: evidenceColumns,
                filterSchemas: evidenceFilterSchemas,
            }),
        ).current;

        const missingEvidencePresenter = useRef(
            new DataTablePresenter<ProviderMissingEvidence>({
                name: 'provider-missing-evidence',
                data: [],
                columns: missingEvidenceColumns,
                filterSchemas: [],
            }),
        ).current;

        useEffect(() => {
            document.title = 'ENTER Platform - Evidence';
        }, []);

        useEffect(() => {
            presenter.onColumnsUpdate(evidenceColumns);
        }, [evidenceColumns, presenter]);

        const onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            setLoading(true);

            try {
                authContext.cancelGroup?.('providerEvidence');
                authContext.setGroup?.('providerEvidence');

                const { sort, order } = parseSortBy(sortBy, evidenceColumns);
                const sortEnums = (sort as (GetProviderEvidenceListSortEnum | undefined)[] | undefined)?.filter(
                    (value): value is GetProviderEvidenceListSortEnum => Boolean(value),
                );
                const orderEnums = order?.map((value) =>
                    value === IItemSortOrder.ASCENDING
                        ? GetProviderEvidenceListOrderEnum.ASCENDING
                        : GetProviderEvidenceListOrderEnum.DESCENDING,
                );

                currentFetchOptionsRef.current = {
                    pageIndex,
                    pageSize,
                    filters: validFilters,
                    sort: sortEnums,
                    order: orderEnums,
                };

                const listing = await rootStore.evidenceStore.loadEvidenceList({
                    page: pageIndex,
                    pageSize,
                    sort: sortEnums,
                    order: orderEnums,
                    filters: validFilters,
                });

                presenter.onDataUpdate(listing.evidence);
                presenter.onDataTotalChange(listing.total ?? listing.evidence.length);
            } catch (error) {
                dialogContext.showError?.(error);
            } finally {
                setLoading(false);
            }
        };

        const onFetchMissingEvidence = async (fetchOptions: ITableFetchOptions): Promise<void> => {
            const { pageIndex, pageSize, sortBy } = fetchOptions;
            setLoading(true);

            try {
                authContext.cancelGroup?.('providerMissingEvidence');
                authContext.setGroup?.('providerMissingEvidence');

                const { sort, order } = parseSortBy(sortBy, missingEvidenceColumns);
                const sortEnums = (sort as (GetProviderEvidenceMissingListSortEnum | undefined)[] | undefined)?.filter(
                    (value): value is GetProviderEvidenceMissingListSortEnum => Boolean(value),
                );
                const orderEnums = order?.map((value) =>
                    value === IItemSortOrder.ASCENDING
                        ? GetProviderEvidenceMissingListOrderEnum.ASCENDING
                        : GetProviderEvidenceMissingListOrderEnum.DESCENDING,
                );

                const listing = await rootStore.evidenceStore.loadMissingEvidenceList({
                    page: pageIndex,
                    pageSize,
                    sort: sortEnums,
                    order: orderEnums,
                });

                missingEvidencePresenter.onDataUpdate(listing.missingEvidence);
                missingEvidencePresenter.onDataTotalChange(listing.total ?? listing.missingEvidence.length);
            } catch (error) {
                dialogContext.showError?.(error);
            } finally {
                setLoading(false);
            }
        };

        const onRowClick = useCallback((row: Row<ProviderEvidence>): void => {
            const evidence = row.original;
            if (!evidence.id) return;

            const { pageIndex, pageSize, filters, sort, order } = currentFetchOptionsRef.current;
            const rowOffset = (pageIndex * pageSize) + row.index;

            rootStore.evidenceStore.setPaginationState({
                offset: rowOffset,
                total: rootStore.evidenceStore.total,
                filters,
                sortRequest: sort && order ? { sort, order } : undefined,
            });

            navigate(accountId ? `/${accountId}/verifications/evidence/${evidence.id}` : `/verifications/evidence/${evidence.id}`);
        }, [rootStore.evidenceStore, navigate, accountId]);

        return (
            <PageLayout>
                <PageHeader title={showMissingEvidence ? 'Missing Evidence' : 'Evidence'} hideDateRange={true} />
                <PageLayoutRow>
                    <S.CheckboxContainer>
                        <BaseCheckbox
                            checked={showMissingEvidence}
                            onChange={(): void => setShowMissingEvidence(!showMissingEvidence)}
                            label="Show Missing Evidence"
                        />
                    </S.CheckboxContainer>
                </PageLayoutRow>
                <PageLayoutRow>
                    <DataTableCard size="large">
                        <S.Wrapper>
                            {showMissingEvidence ? (
                                <DataTable
                                    presenter={missingEvidencePresenter}
                                    isLoading={loading}
                                    onFetch={onFetchMissingEvidence}
                                    hideSelectAll={true}
                                    pageSizeOptions={[25, 50, 100]}
                                    showExport={true}
                                />
                            ) : (
                                <DataTable
                                    presenter={presenter}
                                    isLoading={loading}
                                    onFetch={onFetch}
                                    onRowClick={onRowClick}
                                    hideSelectAll={true}
                                    pageSizeOptions={[25, 50, 100]}
                                    showExport={true}
                                />
                            )}
                        </S.Wrapper>
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }),
);
